/* eslint-disable no-shadow */
import React, { useState, useEffect, useContext, useCallback } from 'react';
import Autosuggest from 'react-autosuggest';
import useAxios from 'axios-hooks';
import { SearchStateContext } from '../../context/SearchContext';
import '../../styles/common/autosuggest.css';

// Components
import LabeledInput from '../LabeledInput';

const SuggestInput = ({ placeholder, field, value, filters, updateFilter }) => {
  const { setWithSearch } = useContext(SearchStateContext);
  const [suggestions, setSuggestions] = useState([]);

  const [{ data: suggestedData }, fetchSuggestions] = useAxios(
    {
      url: `${process.env.REACT_APP_BASE_URL}api/v1/submissions/suggest`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem('jwt')}`
      }
    },
    { manual: true }
  );

  useEffect(() => {
    setWithSearch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (suggestedData) {
      setSuggestions(suggestedData);
    } else {
      setSuggestions([]);
    }
  }, [suggestedData]);

  // Teach Autosuggest how to calculate suggestions for any given input value.
  const setFilter = useCallback(
    ({ value }) => {
      if (value !== '') {
        updateFilter(field, value);
      }
    },
    [filters]
  );

  // Autosuggest will call this function every time you need to update suggestions.
  // eslint-disable-next-line no-unused-vars
  const getSuggestions = useEffect(() => {
    if (
      filters.first_name !== '' ||
      filters.last_name !== '' ||
      filters.student_id !== '' ||
      (filters.provider_name && filters.provider_name !== '')
    ) {
      const params = {
        first_name: filters.first_name || '',
        last_name: filters.last_name || '',
        student_id: filters.student_id || '',
        enrollment_period_segment_id: filters.enrollment_period_segment_id,
        is_active: filters.active ? '1' : '0',
      };

      if (filters.status) {
        params.status = filters.status;
      }

      if (filters.provider_name !== null && filters.provider_name !== undefined) {
        params.provider_name = filters.provider_name;
      }

      if (filters.rollover !== null && filters.rollover !== undefined) {
        params.is_rollover = filters.rollover ? '1' : '0';
      }

      fetchSuggestions({ params });
    }
  }, [filters[field]]);

  // When suggestion is clicked, Autosuggest needs to populate the input
  // based on the clicked suggestion. Teach Autosuggest how to calculate the
  // input value for every given suggestion.
  const getSuggestionValue = (suggestion) => {
    if (field !== 'provider_name') return suggestion.student[field];
    return suggestion[field];
  };

  // Use your imagination to render suggestions.
  const renderSuggestion = (suggestion) => (
    <div>
      {field !== 'provider_name'
        ? suggestion.student[field]
        : suggestion[field]}
    </div>
  );

  const onChange = useCallback((event, { newValue }) => {
    updateFilter(field, newValue);
  }, []);

  // Autosuggest will call this function every time you need to clear suggestions.
  const onSuggestionsClearRequested = useCallback(() => {
    setSuggestions([]);
  }, []);

  // Autosuggest will pass through all these props to the input.
  const inputProps = {
    placeholder: '',
    value,
    onChange
  };

  // Finally, render it!
  return (
    <div className="input-group">
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={setFilter}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
        renderInputComponent={(inputProps) => (
          <LabeledInput
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...inputProps}
            className="form-control bg-transparent"
            label={placeholder}
          />
        )}
      />
    </div>
  );
};

export default SuggestInput;
